// 2026-06-25: Building Edit dialog — same fields as Add, pre-filled from editData.
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, TextField, Button, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Swal from 'sweetalert2';
import MapPicker from '../../Shared/MapPicker';
import './buildings.css';
import { shiftadd } from '../../Services/app/masterservice';

const CONTINENTS = ['Asia', 'Europe', 'Africa', 'North America', 'South America', 'Oceania'];
const COUNTRIES_BY_CONTINENT = {
  Asia: ['India', 'China', 'Japan', 'Thailand', 'Vietnam', 'South Korea', 'Singapore', 'UAE'],
  Europe: ['Germany', 'United Kingdom', 'France', 'Italy', 'Spain', 'Poland', 'Czech Republic'],
  'North America': ['United States', 'Canada', 'Mexico'],
  'South America': ['Brazil', 'Argentina', 'Chile'],
  Africa: ['South Africa', 'Egypt', 'Morocco'],
  Oceania: ['Australia', 'New Zealand'],
};
const STATES_BY_COUNTRY = {
  India: ['Karnataka', 'Tamil Nadu', 'Maharashtra', 'Gujarat', 'Haryana', 'Punjab', 'Delhi', 'Telangana', 'Andhra Pradesh', 'Kerala', 'Rajasthan', 'Uttar Pradesh', 'Madhya Pradesh', 'West Bengal'],
};

export default function BuildingsEdit({ open, handleClose, handleEdit, dialogOpenCount, datasource, customerId, editData }) {
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';

  const [form, setForm] = useState({
    continent: 'Asia', country: 'India', state: 'Karnataka',
    plant_code: '', plant_name: '', plant_city: '', plant_lat: '', plant_lng: '',
    code: '', name: '', floor_count: 1, address: '', gate_number: '',
    utility_meter_ids: '', evacuation_zone: '', sort_order: 1, active: true,
  });

  useEffect(() => {
    if (!editData) return;
    setForm({
      ...editData,
      utility_meter_ids: Array.isArray(editData.utility_meter_ids)
        ? editData.utility_meter_ids.join(', ')
        : (editData.utility_meter_ids || ''),
      plant_lat: editData.plant_lat || '',
      plant_lng: editData.plant_lng || '',
    });
  }, [editData]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  const countries = COUNTRIES_BY_CONTINENT[form.continent] || [];
  const states = STATES_BY_COUNTRY[form.country] || [];

  const onSubmit = async () => {
    if (!form.code || !form.name) {
      Swal.fire('Missing field', 'Code and Name are required.', 'warning');
      return;
    }
    const now = Date.now();
    const updatedBuilding = {
      ...form,
      utility_meter_ids: typeof form.utility_meter_ids === 'string'
        ? form.utility_meter_ids.split(',').map(s => s.trim()).filter(Boolean)
        : (form.utility_meter_ids || []),
      floor_count: Number(form.floor_count) || 1,
      plant_lat: form.plant_lat ? Number(form.plant_lat) : null,
      plant_lng: form.plant_lng ? Number(form.plant_lng) : null,
      sort_order: Number(form.sort_order) || 0,
      updated_at: now,
    };
    try {
      const all = (datasource || []).map(b =>
        (b.id || b.code) === (editData.id || editData.code) ? updatedBuilding : b
      );
      await shiftadd({ buildings: all }, customerId, 'SERVER_SCOPE');
      Swal.fire({ icon: 'success', title: 'Site updated', timer: 1100, showConfirmButton: false });
      handleEdit();
    } catch (e) {
      console.error('Building edit error', e);
      Swal.fire('Error', e?.response?.data?.message || e.message || 'Save failed', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ style: { background: dialogBackgroundColor } }}>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Edit Site — {editData?.code}</span>
          <Tooltip title="Close"><IconButton onClick={handleClose}><CloseIcon /></IconButton></Tooltip>
        </div>
      </DialogTitle>

      <div style={{ padding: '8px 24px 24px' }}>
        <Accordion sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong>Geography</strong>
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>{form.continent} · {form.country} · {form.state}</span>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Continent</InputLabel>
                <Select label="Continent" value={form.continent} onChange={set('continent')}>
                  {CONTINENTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Country</InputLabel>
                <Select label="Country" value={form.country} onChange={set('country')}>
                  {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>State</InputLabel>
                <Select label="State" value={form.state} onChange={set('state')}>
                  {states.length === 0 && <MenuItem value={form.state}>{form.state || '—'}</MenuItem>}
                  {states.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 1, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong>Plant Info</strong>
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>{form.plant_name || '(unnamed)'} · {form.plant_city || ''}</span>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <TextField size="small" label="Plant Code" value={form.plant_code} onChange={set('plant_code')} fullWidth />
              <TextField size="small" label="Plant Name" value={form.plant_name} onChange={set('plant_name')} fullWidth />
              <TextField size="small" label="City" value={form.plant_city} onChange={set('plant_city')} fullWidth />
              <div />
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>
                  Plant Location (click on map to set, or use search)
                </label>
                <MapPicker
                  lat={form.plant_lat}
                  lng={form.plant_lng}
                  onChange={(la, lo) => setForm(prev => ({ ...prev, plant_lat: la, plant_lng: lo }))}
                  height={280}
                  searchHint={form.plant_city ? form.plant_city + ', ' + (form.country || 'India') : 'Search city or address...'}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded sx={{ mb: 1, boxShadow: 'none', border: '1.5px solid #f97316' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <strong style={{ color: '#f97316' }}>Building Details</strong>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <TextField size="small" label="Code *" value={form.code} onChange={set('code')} required fullWidth disabled />
              <TextField size="small" label="Name *" value={form.name} onChange={set('name')} required fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Floor count</InputLabel>
                <Select label="Floor count" value={form.floor_count} onChange={set('floor_count')}>
                  {[1, 2, 3, 4, 5, 6].map(n => <MenuItem key={n} value={n}>{n} floor{n > 1 ? 's' : ''}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Gate Number" value={form.gate_number} onChange={set('gate_number')} fullWidth />
              <TextField size="small" label="Address" value={form.address} onChange={set('address')} fullWidth style={{ gridColumn: 'span 2' }} />
              <TextField size="small" label="Utility Meter IDs (comma-separated)" value={form.utility_meter_ids} onChange={set('utility_meter_ids')} fullWidth />
              <TextField size="small" label="Evacuation Zone" value={form.evacuation_zone} onChange={set('evacuation_zone')} fullWidth />
              <TextField size="small" label="Sort order" type="number" value={form.sort_order} onChange={set('sort_order')} fullWidth />
            </div>
          </AccordionDetails>
        </Accordion>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={handleClose} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={onSubmit} variant="contained" style={{ background: '#f97316' }}>Save Changes</Button>
        </div>
      </div>
    </Dialog>
  );
}
